import {
  customElement,
  FASTElement,
  observable,
  Notifier,
  Subscriber,
  Observable,
  Splice,
} from '@microsoft/fast-element';
import { SellerInterestTemplate as template } from './seller-interest.template';
import { SellerInterestStyles as styles } from './seller-interest.styles';
import { IRfqService, IVppaService } from '../../../../services';
import { ZeroGridPro as zeroAgGrid } from '@genesislcap/foundation-zero-grid-pro';
import { updateArray } from '../../../../utils/dataserver';
import { logger, Message, SocketSubscription } from '@genesislcap/foundation-comms';
import {
  PRODUCT_TYPE,
  TECHNOLOGY,
  VPPA_RFQ_STATUS,
  RFQ_APPLICABLE_REC_PROGRAM,
} from '../../../../utils';
import { IRfq, IVppa } from '../../../../types';
import { IMultiSelectOption, MultiSelect } from '../../../common/multi-select';
import { CASCADE_ENUM, EnumTranslate, IEnumService } from '../../../../services/enum.service';
import { COLUMN_FIELDS } from './column-config';
import { getGroupColumnSameObject } from '../../../../utils/grid';
import {
  getFormattedDate,
  getEnumReadableValue,
  getCreditProfileValue,
} from '../../../../utils/formatting';
import { ICompanyService } from '../../../../services/company.service';

const name = 'seller-interest';

export interface VppaCriteriaFromRfqs {
  technology: string;
  iso: string;
  settlementPoint: string;
  tenorStartDate: number | Date;
  tenorMonths: number;
  priceLowerBound: number;
  priceUpperBound: number;
  requestedVolume: number;
  uniqueId: string;
  applicableRECProgram: string;
  productType: string;
  contractType: string;
}

@customElement({
  name,
  template,
  styles,
})
export class SellerInterest extends FASTElement {
  @IVppaService vppaService: IVppaService;
  @IRfqService rfqService: IRfqService;
  @IEnumService enumService: IEnumService;
  @ICompanyService companyService: ICompanyService;

  @observable vppaCriteriaFromRfqsList: VppaCriteriaFromRfqs[];
  @observable sellerInterestVppaGrid: zeroAgGrid;
  @observable rfqSubscription: SocketSubscription;
  @observable sellerInterestVppaSubscription: SocketSubscription;
  @observable vppaSubscription: SocketSubscription;
  @observable rfqsElement: MultiSelect;
  @observable isExpanded: boolean = false;

  @observable applicableRECProgramEnums: Array<EnumTranslate> = [];

  private sellerInterestVppaList: IVppa[];
  private rfqList: IRfq[];
  private _rfqsList: Array<IMultiSelectOption> = [];
  private selectedRfqs: Array<String> = [];

  rfqNotifier: Notifier;
  rfqHandler: Subscriber;

  connectedCallback() {
    super.connectedCallback();
    this.getAllRfqs();
    this.rfqListener();
    this.loadEnums();
    const gridOptions = {
      animateRows: true,
      suppressCellFocus: true,
      suppressBrowserResizeObserver: true,
      scrollbarWidth: 10,
      onRowSelected: (params) => this.rowSelected(params),
      tooltipShowDelay: 300,
      defaultColDef: {
        resizable: true,
        wrapHeaderText: true,
        autoHeaderHeight: true,
      },
    };

    this.sellerInterestVppaGrid.gridOptions = gridOptions;
  }

  private rfqListener() {
    const _this = this;
    this.rfqNotifier = Observable.getNotifier(this.rfqsElement);
    this.rfqHandler = {
      handleChange(source: any, splices: Splice[]) {
        _this.rfqSelectionChanged(_this.rfqsElement.getSelectedIds());
      },
    };

    this.rfqNotifier.subscribe(this.rfqHandler, 'selectedIds');
  }

  public onGridReady() {
    this.sellerInterestVppaGrid.gridApi.setRowData([]);
    this.changeExpandedColumnsVisibility(false);
  }

  public rfqSelectionChanged(data: Array<string>) {
    if (this.selectedRfqs.length !== data.length) {
      this.selectedRfqs = data;
      this.updateVppaCriteria();
    }
  }

  public expandView() {
    this.isExpanded = !this.isExpanded;
    this.changeExpandedColumnsVisibility(this.isExpanded);
    this.$emit('expandSellerInterest', this.isExpanded);
  }

  public rowSelected(event) {
    if (event && event.node) {
      if (event.node.selected) {
        this.$emit('vppaSelected', event.data);
      }
    }
  }

  private async loadEnums() {
    await this.enumService
      .getFilteredEnumValues(CASCADE_ENUM.APPLICABLE_REC_PROGRAM)
      .then((response) => {
        if (response.REPLY) {
          this.applicableRECProgramEnums = response.REPLY;
        }
      });
  }

  private populateGrid() {
    let resultList = [];
    this.sellerInterestVppaList = [];
    this.sellerInterestVppaSubscription?.unsubscribe();

    this.sellerInterestVppaSubscription = this.vppaService
      .getAllVppas(this.getVppaCriteria())
      .subscribe({
        next: (streamMessage: Message) => {
          resultList = streamMessage.ROW ?? null;

          if (resultList) {
            this.sellerInterestVppaList = updateArray(resultList, this.sellerInterestVppaList).sort(
              (a, b) => a.MODIFIED_AT - b.MODIFIED_AT,
            );
            this.sellerInterestVppaGrid.gridApi.setRowData(this.sellerInterestVppaList);
          } else {
            this.sellerInterestVppaGrid.gridApi.setRowData(this.sellerInterestVppaList);
            const resultERROR = streamMessage.DETAILS?.ERROR;
            logger.error(resultERROR);
          }
        },
        error: (error: any) => {
          this.sellerInterestVppaGrid.gridApi.setRowData(this.sellerInterestVppaList);
          logger.error(error);
        },
      });
  }

  private updateVppaCriteria() {
    if (this.selectedRfqs && this.selectedRfqs.length > 0) {
      const currentlySelectedRfqs = [];
      this.selectedRfqs.forEach((rfqId) => {
        currentlySelectedRfqs.push(this.rfqList.find((rfq) => rfq.RFQ_ID === rfqId));
      });
      this.getVppaCriteriaFromRfqsList(currentlySelectedRfqs);
    } else {
      this.getVppaCriteriaFromRfqsList(this.rfqList);
    }
  }

  private getVppaCriteria() {
    let finalCriteria = `(VPPA_STATUS == '${VPPA_RFQ_STATUS.ACTIVE}' || VPPA_STATUS == '${VPPA_RFQ_STATUS.TRADE_PENDING}' || VPPA_STATUS == '${VPPA_RFQ_STATUS.EXISTING_INTEREST}')`;
    let selectedCriteria = '';
    const START_PARENTHESES = '(';
    const END_PARENTHESES = ')';
    const OR_OPERATOR = ` || `;
    this.vppaCriteriaFromRfqsList.forEach((matchCriteria) => {
      let criteria = '';
      let applicableRECProgram = '';

      const technology =
        matchCriteria.technology && matchCriteria.technology !== TECHNOLOGY.BOTH
          ? `TECHNOLOGY == '${matchCriteria.technology}' && `
          : '';

      const iso = matchCriteria.iso ? `ISO == '${matchCriteria.iso}' && ` : '';
      const productType = matchCriteria.productType
        ? `PRODUCT_TYPE == '${matchCriteria.productType}' && `
        : '';
      const contractType = matchCriteria.contractType
        ? `CONTRACT_TYPE == '${matchCriteria.contractType}' && `
        : '';
      const settlementPoint = matchCriteria.settlementPoint
        ? `SETTLEMENT_POINT == '${matchCriteria.settlementPoint}' && `
        : '';
      if (
        matchCriteria.applicableRECProgram &&
        matchCriteria.applicableRECProgram !== RFQ_APPLICABLE_REC_PROGRAM.ANY
      ) {
        applicableRECProgram = `APPLICABLE_REC_PROGRAM == '${matchCriteria.applicableRECProgram}' && `;
      }
      criteria = ''.concat(
        START_PARENTHESES,
        technology,
        iso,
        productType,
        applicableRECProgram,
        contractType,
      );
      if (matchCriteria.productType !== PRODUCT_TYPE.RECS) {
        criteria = criteria.concat(settlementPoint);
      }

      if (criteria.length > 0) {
        // This slice is to remove the last "&&"" of the criteria
        criteria = criteria.slice(0, -4);
        criteria = criteria.concat(END_PARENTHESES);
      }

      if (this.vppaCriteriaFromRfqsList.at(-1) != matchCriteria) {
        criteria = criteria.concat(OR_OPERATOR);
      }

      selectedCriteria = selectedCriteria.concat(criteria);
    });
    if (selectedCriteria.length > 0) {
      finalCriteria = finalCriteria.concat(` && (${selectedCriteria})`);
    }

    finalCriteria = finalCriteria.concat(
      ` && CASCADE_COMPANY_ID != '${this.companyService.getCurrentCompany().CASCADE_COMPANY_ID}'`,
    );
    return finalCriteria;
  }

  private changeExpandedColumnsVisibility(isColumnsVisible: boolean) {
    this.sellerInterestVppaGrid['agGridOptions'].columnApi.setColumnVisible(
      'UNIQUE_ID',
      isColumnsVisible,
    );
    this.sellerInterestVppaGrid['agGridOptions'].columnApi.setColumnVisible(
      'TENOR_START_DATE',
      isColumnsVisible,
    );
    this.sellerInterestVppaGrid['agGridOptions'].columnApi.setColumnVisible(
      'TENOR_MONTHS',
      isColumnsVisible,
    );
    this.sellerInterestVppaGrid['agGridOptions'].columnApi.setColumnVisible(
      'SELLER_POST_COD_COLLATERAL',
      isColumnsVisible,
    );
    this.sellerInterestVppaGrid['agGridOptions'].columnApi.setColumnVisible(
      'LME',
      isColumnsVisible,
    );
    this.sellerInterestVppaGrid['agGridOptions'].columnApi.setColumnVisible(
      'IMPLIED_CARBON_PRICE',
      isColumnsVisible,
    );
    this.sellerInterestVppaGrid['agGridOptions'].columnApi.setColumnVisible(
      'VPPA_STATUS',
      isColumnsVisible,
    );
    this.sellerInterestVppaGrid['agGridOptions'].columnApi.setColumnVisible(
      'APPLICABLE_REC_PROGRAM',
      isColumnsVisible,
    );
    if (isColumnsVisible) {
      // Autosize when expanding seller interest
      this.sellerInterestVppaGrid.columnApi.autoSizeAllColumns();
    }
  }

  private getAllRfqs() {
    const rfqStatusCriteria = `(RFQ_STATUS == '${VPPA_RFQ_STATUS.ACTIVE}' || RFQ_STATUS == '${VPPA_RFQ_STATUS.TRADE_PENDING}' || RFQ_STATUS == '${VPPA_RFQ_STATUS.EXISTING_INTEREST}')`;

    let resultList = [];
    this.rfqSubscription?.unsubscribe();

    this.rfqSubscription = this.rfqService.getAllRfqs(rfqStatusCriteria).subscribe({
      next: (streamMessage: Message) => {
        resultList = streamMessage.ROW ?? null;

        if (resultList) {
          this.rfqList = updateArray(resultList, this.rfqList).sort(
            (a, b) => b.MODIFIED_AT - a.MODIFIED_AT,
          );
          this.generateRfqDD();
          this.updateVppaCriteria();
        } else {
          this.rfqList = [];
          const resultERROR = streamMessage.DETAILS?.ERROR;
          logger.error(resultERROR);
        }
      },
      error: (error: any) => {
        this.rfqList = [];
        logger.error(error);
      },
    });
  }

  public getVppaCriteriaFromRfqsList(rfqList: IRfq[]) {
    this.vppaCriteriaFromRfqsList = [];

    if (rfqList) {
      rfqList.forEach((rfq) => {
        this.vppaCriteriaFromRfqsList.push({
          technology: rfq.TECHNOLOGY,
          iso: rfq.ISO,
          settlementPoint: rfq.SETTLEMENT_POINT,
          tenorStartDate: rfq.TENOR_START_DATE,
          tenorMonths: rfq.TENOR_MONTHS,
          priceLowerBound: rfq.FIXED_PRICE_LOWER_BOUND,
          priceUpperBound: rfq.FIXED_PRICE_UPPER_BOUND,
          requestedVolume: rfq.REQUESTED_VOLUME,
          uniqueId: rfq.UNIQUE_ID,
          applicableRECProgram: rfq.RFQ_APPLICABLE_REC_PROGRAM,
          productType: rfq.PRODUCT_TYPE,
          contractType: rfq.CONTRACT_TYPE,
        });
      });
    }
    this.populateGrid();
    this.updateGridCsv();
  }

  private generateRfqDD() {
    this._rfqsList = [
      ...new Set(
        this.rfqList.map((item) => {
          const value: IMultiSelectOption = {
            CHECKED: false,
            ITEM_ID: item.RFQ_ID,
            ITEM_DESC: item.TICKER.concat('-').concat(item.UNIQUE_ID),
            DISABLED: false,
            CATEGORY: '',
          };
          return value;
        }),
      ),
    ];

    this.rfqsElement.setDataList(this._rfqsList);
  }

  public downloadGridCsv() {
    const csvParams = {
      fileName: 'Seller Interest',
      columnKeys: [
        COLUMN_FIELDS.TICKER,
        COLUMN_FIELDS.PROJECT_NAME,
        COLUMN_FIELDS.UNIQUE_ID,
        COLUMN_FIELDS.ISO,
        COLUMN_FIELDS.SETTLEMENT_POINT,
        COLUMN_FIELDS.PRICE,
        COLUMN_FIELDS.OFFERED_CAPACITY,
        COLUMN_FIELDS.TENOR_START_DATE,
        COLUMN_FIELDS.TENOR_MONTHS,
        COLUMN_FIELDS.COLLATERAL,
        COLUMN_FIELDS.LME,
        COLUMN_FIELDS.IMPLIED_CARBON_PRICE,
        COLUMN_FIELDS.VPPA_STATUS,
        COLUMN_FIELDS.APPLICABLE_REC_PROGRAM,
      ],
      processCellCallback: (params) => {
        let formattedValue = params;

        if (params.column.getColId() === COLUMN_FIELDS.TICKER) {
          formattedValue = params.value;
        } else if (params.column.getColId() === COLUMN_FIELDS.PROJECT_NAME) {
          formattedValue = JSON.parse(params.value).NAME;
        } else if (params.column.getColId() === COLUMN_FIELDS.UNIQUE_ID) {
          formattedValue = params.value;
        } else if (params.column.getColId() === COLUMN_FIELDS.ISO) {
          formattedValue = params.value;
        } else if (params.column.getColId() === COLUMN_FIELDS.SETTLEMENT_POINT) {
          formattedValue = params.value;
        } else if (params.column.getColId() === COLUMN_FIELDS.PRICE) {
          formattedValue = params.value;
        } else if (params.column.getColId() === COLUMN_FIELDS.TENOR_START_DATE) {
          formattedValue = params.value
            ? getFormattedDate(params.value)
            : getFormattedDate(
                getGroupColumnSameObject(params, COLUMN_FIELDS.VPPA_OBJECT).TENOR_START_DATE,
              );
        } else if (params.column.getColId() === COLUMN_FIELDS.OFFERED_CAPACITY) {
          formattedValue = params.value;
        } else if (params.column.getColId() === COLUMN_FIELDS.TENOR_MONTHS) {
          formattedValue = params.value
            ? params.value
            : getGroupColumnSameObject(params, COLUMN_FIELDS.VPPA_OBJECT).TENOR_MONTHS;
        } else if (params.column.getColId() === COLUMN_FIELDS.COLLATERAL) {
          let vppaCreditProfile = getGroupColumnSameObject(
            params,
            COLUMN_FIELDS.VPPA_OBJECT,
          ).VPPA_CREDIT_PROFILE;
          let vppaCollateral = getGroupColumnSameObject(
            params,
            COLUMN_FIELDS.VPPA_OBJECT,
          ).SELLER_POST_COD_COLLATERAL;
          getCreditProfileValue(vppaCreditProfile, vppaCollateral);
          formattedValue = params.data
            ? getCreditProfileValue(vppaCreditProfile, params.data.COLLATERAL)
            : getCreditProfileValue(vppaCreditProfile, vppaCollateral);
        } else if (params.column.getColId() === COLUMN_FIELDS.LME) {
          formattedValue = params.value;
        } else if (params.column.getColId() === COLUMN_FIELDS.IMPLIED_CARBON_PRICE) {
          formattedValue = params.value;
        } else if (params.column.getColId() === COLUMN_FIELDS.VPPA_STATUS) {
          formattedValue = params.value;
        } else if (params.column.getColId() === COLUMN_FIELDS.APPLICABLE_REC_PROGRAM) {
          formattedValue = getEnumReadableValue(this.applicableRECProgramEnums, params.value);
        }
        return formattedValue;
      },
    };
    this.sellerInterestVppaGrid.gridApi.exportDataAsCsv(csvParams);
  }

  private updateGridCsv() {
    this.sellerInterestVppaGrid.gridApi?.getDataAsCsv();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }
}
